import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type {
  ApiClient, BerichtsfassungenSeite, BerichtsfreigabeStand, Freigabeschritt, Registerfassung,
} from '../api';
import {
  berichtsfassung, ContentSection, DataTable, ErrorState, StatusBadge, type Spalte,
} from '../muster';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SEITE „BERICHTE“ (TX2, Befund P9; TX-STANDARD S3.6)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Das Berichtsregister des Projekts: jede Fassung mit Nummer, Gegenstand,
 * Stand, Freigabestatus und Ablage. Eine ABGELEGTE Fassung lässt sich hier
 * unverändert herunterladen - genau die Bytes, die beim Erzeugen ausgeliefert
 * wurden, sha-geprüft vom Server. Das zieht keine neue Nummer und schreibt
 * nichts.
 *
 * Bis TX2 gab es dafür nur die API (`/api/projects/{id}/berichte[/{nr}.pdf]`,
 * F1) und das Werkzeug `bericht-abrufen`: Wer die verschickte Fassung noch
 * einmal brauchte, erzeugte auf der Fachseite eine neue.
 *
 * Blättern: Der Server liefert höchstens 500 Zeilen je Abruf, jüngste zuerst.
 * Gesucht und sortiert wird in den geladenen Zeilen; „weitere laden“ holt die
 * nächste Seite dazu. Gewerk und „nur abgelegte“ filtert der Server.
 *
 * N7b-2 (QUE-13): Ist der Freigabeweg an (Schalter `Berichte:Freigabeweg`), steht eine Spalte „Freigabe“ da - Zustand
 * des Wegs (geprüft, freigegeben, daraus entstandene Fassung) und nur die Knöpfe, die der Server der angemeldeten
 * Person erlaubt (Vier-Augen, Rolle `Freigeber.<Gewerk>`). Eine Begründung kommt in ein beschriftetes Feld, kein
 * `window.prompt`. Ist der Weg aus, fehlt die Spalte.
 */

const SEITE = 500;

/** Mindestlänge einer Begründung (Freigaberegeln.MindestBegruendung, R3/R5). */
const MINDEST_BEGRUENDUNG = 20;

const SCHRITTTEXT: Record<Freigabeschritt, string> = { pruefen: 'prüfen', freigeben: 'freigeben', zurueckziehen: 'zurückziehen' };

const ZEIT = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/** „17.10.2026, 14:05“ - leer bei einer Fehlstelle. */
export function zeitpunkt(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : ZEIT.format(d);
}

/** Die Dateigröße in kB mit einer Nachkommastelle, deutsch formatiert. */
export function groesseKb(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '';
  return `${(bytes / 1024).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kB`;
}

export function Berichte({ api, projektId }: { api: ApiClient; projektId: string }) {
  const hinweisId = useId();
  const [zeilen, setZeilen] = useState<Registerfassung[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [seite, setSeite] = useState(0);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<unknown>(null);
  const [gewerk, setGewerk] = useState('');
  const [nurAbgelegt, setNurAbgelegt] = useState(false);
  const [gewerke, setGewerke] = useState<string[]>([]);
  const [laden, setLaden] = useState<string | null>(null);
  const [ladefehler, setLadefehler] = useState<{ nummer: string; text: string } | null>(null);
  const begruendungId = useId();
  const [freigabe, setFreigabe] = useState<Map<string, BerichtsfreigabeStand> | null>(null);
  const [schritt, setSchritt] = useState<{ nummer: string; schritt: Freigabeschritt } | null>(null);
  const [begruendung, setBegruendung] = useState('');
  const [schrittLaeuft, setSchrittLaeuft] = useState(false);
  const [schrittfehler, setSchrittfehler] = useState<{ nummer: string; text: string } | null>(null);

  const freigabeHolen = useCallback(() => {
    api.berichtsfreigabestand(projektId)
      .then((s) => setFreigabe(s === null ? null : new Map(s.map((z) => [z.dokumentnummer, z]))))
      .catch(() => setFreigabe(null));
  }, [api, projektId]);

  useEffect(() => { freigabeHolen(); }, [freigabeHolen]);

  const schrittGehen = useCallback((nummer: string, art: Freigabeschritt, grund?: string) => {
    setSchrittLaeuft(true);
    setSchrittfehler(null);
    api.berichtsfreigabeSchritt(projektId, nummer, art, grund)
      .then(() => { setSchritt(null); setBegruendung(''); freigabeHolen(); })
      .catch((e: unknown) => setSchrittfehler({ nummer, text: e instanceof Error ? e.message : String(e) }))
      .finally(() => setSchrittLaeuft(false));
  }, [api, projektId, freigabeHolen]);

  // Prüfen braucht keine Begründung; Freigeben nur bei Vorbehalt; Zurückziehen immer (R3/R5).
  const schrittWaehlen = useCallback((st: BerichtsfreigabeStand, art: Freigabeschritt) => {
    if (art === 'pruefen' || (art === 'freigeben' && !st.begruendungNoetig)) {
      schrittGehen(st.dokumentnummer, art);
      return;
    }
    setSchrittfehler(null);
    setBegruendung('');
    setSchritt({ nummer: st.dokumentnummer, schritt: art });
  }, [schrittGehen]);

  const holen = useCallback((nr: number, anhaengen: boolean) => {
    setLaedt(true);
    setFehler(null);
    api.berichtsfassungen(projektId, {
      gewerk: gewerk || undefined,
      abgelegt: nurAbgelegt ? true : undefined,
      seite: nr,
      groesse: SEITE,
    })
      .then((s: BerichtsfassungenSeite) => {
        setZeilen((alt) => (anhaengen ? [...alt, ...s.zeilen] : s.zeilen));
        setGesamt(s.gesamt);
        setSeite(nr);
        // Die Gewerke für die Auswahl kommen aus dem ungefilterten Bestand
        // und bleiben stehen, solange gefiltert wird.
        if (!gewerk) {
          setGewerke((alt) => [...new Set([...alt, ...s.zeilen.map((z) => z.gewerkKuerzel)])].sort());
        }
      })
      .catch(setFehler)
      .finally(() => setLaedt(false));
  }, [api, projektId, gewerk, nurAbgelegt]);

  useEffect(() => { holen(1, false); }, [holen]);

  const herunterladen = useCallback((z: Registerfassung) => {
    setLaden(z.dokumentnummer);
    setLadefehler(null);
    api.berichtsfassungLaden(projektId, z.dokumentnummer)
      .catch((e: unknown) => setLadefehler({
        nummer: z.dokumentnummer, text: e instanceof Error ? e.message : String(e),
      }))
      .finally(() => setLaden(null));
  }, [api, projektId]);

  const spalten = useMemo<Spalte<Registerfassung>[]>(() => [
    { schluessel: 'nummer', titel: 'Dokumentnummer', wert: (z) => z.dokumentnummer, immerSichtbar: true },
    { schluessel: 'gewerk', titel: 'Gewerk', wert: (z) => z.gewerkKuerzel, titelHinweis: 'Gewerkkürzel' },
    { schluessel: 'art', titel: 'Art', wert: (z) => `${z.artKuerzel} · ${z.berechnungsart}` },
    { schluessel: 'gegenstand', titel: 'Gegenstand', wert: (z) => z.gegenstand },
    { schluessel: 'stand', titel: 'Stand', wert: (z) => z.berechnungsstand },
    {
      schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.freigabestatus,
      inhalt: (z) => <StatusBadge {...berichtsfassung(z.freigabestatus)} klein />,
    },
    ...(freigabe === null ? [] : [{
      schluessel: 'freigabe', titel: 'Freigabe', art: 'status' as const, sortierbar: false,
      wert: (z: Registerfassung) => freigabe.get(z.dokumentnummer)?.zustand ?? '',
      inhalt: (z: Registerfassung) => {
        const st = freigabe.get(z.dokumentnummer);
        if (!st) return null;
        const knopf = (art: Freigabeschritt, text: string) => (
          <button
            type="button" className="m-knopf" disabled={schrittLaeuft}
            aria-label={`Fassung ${st.dokumentnummer} ${text}`}
            onClick={() => schrittWaehlen(st, art)}
          >
            {text}
          </button>
        );
        return (
          <span>
            {st.zustand === 'freigegeben' && <StatusBadge zustand="gut" text="freigegeben" klein />}
            {st.zustand === 'geprueft' && <StatusBadge zustand="neutral" text="geprüft" klein />}
            {st.freigegebeneFassung && <span className="m-leise"> → {st.freigegebeneFassung}</span>}
            {st.darfPruefen && <> {knopf('pruefen', 'prüfen')}</>}
            {st.darfFreigeben && <> {knopf('freigeben', 'freigeben')}</>}
            {st.darfZurueckziehen && <> {knopf('zurueckziehen', 'zurückziehen')}</>}
          </span>
        );
      },
    } satisfies Spalte<Registerfassung>]),
    {
      schluessel: 'erzeugt', titel: 'Erzeugt', art: 'datum',
      wert: (z) => new Date(z.erzeugtAm), inhalt: (z) => zeitpunkt(z.erzeugtAm),
    },
    {
      schluessel: 'ablage', titel: 'Ablage', art: 'status', wert: (z) => z.abgelegt,
      inhalt: (z) => (z.abgelegt
        ? <StatusBadge zustand="gut" text="abgelegt" klein />
        : (
          <span>
            <StatusBadge zustand="neutral" text="nicht abgelegt" klein />
            {z.ablagehinweis && <span className="m-leise"> {z.ablagehinweis}</span>}
          </span>
        )),
    },
    {
      schluessel: 'groesse', titel: 'Größe', art: 'zahl', ausrichtung: 'rechts',
      wert: (z) => z.dateiBytes, inhalt: (z) => groesseKb(z.dateiBytes),
    },
    { schluessel: 'datenhash', titel: 'Datenhash', wert: (z) => z.datenhash.slice(0, 16), versteckt: true },
    { schluessel: 'erzeugtVon', titel: 'Erzeugt von', wert: (z) => z.erzeugtVon, versteckt: true },
    {
      schluessel: 'pdf', titel: 'PDF', sortierbar: false, immerSichtbar: true,
      inhalt: (z) => (
        <button
          type="button" className="m-knopf"
          disabled={!z.abgelegt || laden !== null}
          aria-label={`Fassung ${z.dokumentnummer} als PDF herunterladen`}
          aria-describedby={z.abgelegt ? hinweisId : undefined}
          onClick={() => herunterladen(z)}
        >
          {laden === z.dokumentnummer ? 'wird geladen …' : 'PDF'}
        </button>
      ),
    },
  ], [herunterladen, laden, hinweisId, freigabe, schrittLaeuft, schrittWaehlen]);

  const begruendungReicht = begruendung.trim().length >= MINDEST_BEGRUENDUNG;

  const weitere = zeilen.length < gesamt;

  return (
    <ContentSection
      titel="Berichte"
      beschreibung={'Alle Fassungen des Berichtsregisters, jüngste zuerst. Eine abgelegte Fassung '
        + 'kommt hier unverändert zurück - mit ihrer Nummer, ohne neue Fassung.'}
    >
      <p id={hinweisId} className="m-leise">
        Der Abruf liefert die abgelegte Datei und zieht keine neue Nummer.
      </p>

      {ladefehler && (
        <ErrorState
          klein
          meldung={`Fassung ${ladefehler.nummer} konnte nicht geladen werden.`}
          technisch={ladefehler.text}
        />
      )}

      {schrittfehler && (
        <ErrorState
          klein
          meldung={`Der Freigabeschritt für Fassung ${schrittfehler.nummer} ist nicht gelungen.`}
          technisch={schrittfehler.text}
        />
      )}

      {schritt && (
        <div role="group" aria-label={`Begründung: Fassung ${schritt.nummer} ${SCHRITTTEXT[schritt.schritt]}`}>
          <label htmlFor={begruendungId}>
            Begründung für „{SCHRITTTEXT[schritt.schritt]}“ von Fassung {schritt.nummer}
            {' '}(mindestens {MINDEST_BEGRUENDUNG} Zeichen)
          </label>
          <textarea
            id={begruendungId} name="begruendung" rows={3} value={begruendung}
            onChange={(e) => setBegruendung(e.target.value)}
          />
          <button
            type="button" className="m-knopf" name="schrittSenden" disabled={!begruendungReicht || schrittLaeuft}
            onClick={() => schrittGehen(schritt.nummer, schritt.schritt, begruendung.trim())}
          >
            {schrittLaeuft ? 'wird gespeichert …' : SCHRITTTEXT[schritt.schritt]}
          </button>
          {' '}
          <button type="button" className="m-knopf" disabled={schrittLaeuft}
            onClick={() => { setSchritt(null); setBegruendung(''); }}>
            abbrechen
          </button>
        </div>
      )}

      <DataTable<Registerfassung>
        name="Berichtsregister"
        spalten={spalten}
        zeilen={zeilen}
        schluessel={(z) => z.dokumentnummer}
        laedt={laedt && zeilen.length === 0}
        fehler={fehler}
        beiErneutVersuchen={() => holen(1, false)}
        trefferText={`${zeilen.length.toLocaleString('de-DE')} von ${gesamt.toLocaleString('de-DE')} Fassungen geladen`}
        suchePlatzhalter="Nummer, Gegenstand, Stand …"
        ausfuhr
        ausfuhrDateiname="berichtsregister.csv"
        sortierung={{ spalte: 'erzeugt', richtung: 'ab' }}
        werkzeuge={(
          <>
            <label>
              Gewerk{' '}
              <select name="gewerk" value={gewerk} onChange={(e) => setGewerk(e.target.value)}>
                <option value="">alle</option>
                {gewerke.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
            <label>
              <input type="checkbox" name="nurAbgelegt" checked={nurAbgelegt}
                onChange={(e) => setNurAbgelegt(e.target.checked)} />
              {' '}nur abgelegte
            </label>
          </>
        )}
      />

      {weitere && (
        <button type="button" className="m-knopf" disabled={laedt} onClick={() => holen(seite + 1, true)}>
          {laedt ? 'wird geladen …' : `weitere ${Math.min(SEITE, gesamt - zeilen.length).toLocaleString('de-DE')} laden`}
        </button>
      )}
    </ContentSection>
  );
}
